import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatAgentPanel from './ChatAgentPanel';
import UserPurchasedAppsPage from './UserPurchasedAppsPage';
import CaseStudiesPage from '../../case-study/components/CaseStudiesPage';
import { onboardingAPI } from '../../onboarding/api/onboardingAPI';
import ChooseExamDialog from './ChooseExamDialog';
import LeavingYearPrompt from '../../onboarding/components/LeavingYearPrompt';
import { isPathbuddyHost } from '../../../config/eduHost';
import { useAuth } from '../../auth/hooks/useAuth';
import { authAPI } from '../../auth/api/authAPI';
import { isInstructorAccount } from '../../../utils/isInstructorAccount';
import './ChatShell.css';

export const ONBOARDING_COMPLETE_EVENT = 'ivori:onboarding-complete';

function tabFromPathname(pathname) {
  if (pathname === '/apps' || pathname.startsWith('/apps/')) return 'apps';
  if (pathname === '/cases' || pathname.startsWith('/cases/')) return 'cases';
  return 'chat';
}

// Shared shell for /chat, /apps, and /cases. Active pane follows the URL so
// the address bar stays in sync; a layout route keeps this mounted across
// those three paths (apps/cases keep their own scroll/state). Returning to
// Chat keeps the screen that was left: the ordinary conversation lives in
// the mounted panel, and the Paths query (?path= / ?plan=) is remembered
// here and put back on /chat. An explicit /chat?path=… link wins over that
// memory, and so does location state startNewChat or openOnboarding.
// Bottom nav is the shared black/white Sidebar footer (not the cream TabBar).
//
// Onboarding lock for Apps/Cases lives on the Sidebar footer; this shell
// still fetches completion so deep-links to /apps|/cases redirect to /chat
// until setup finishes, and fires ONBOARDING_COMPLETE_EVENT so the footer
// unlocks immediately when ChatAgentPanel saves the final step.
function ChatShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, updateUser } = useAuth();
  const activeTab = tabFromPathname(location.pathname);
  const instructorCasesPermitted = isAuthenticated && isInstructorAccount(user);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  // A school student with no leaving year (ported from the old groups).
  // Asked only once onboarding is complete: during onboarding the
  // conversation asks leavingYear itself.
  const [needsLeavingYear, setNeedsLeavingYear] = useState(false);
  // Bumped only for an explicit startNewChat location state. Returning to
  // the Chat tab does not send that state.
  const [newChatRequest, setNewChatRequest] = useState(0);
  // Last /chat query while the chat tab was showing, including ''. Tab
  // navigations call navigate('/chat') with no query, which would drop a
  // Paths surface. pendingRestoreRef is set on leaving Chat so an empty
  // /chat on the way back is put back, and a search cleared while the user
  // stays on Chat (leaving a path for an ordinary chat) is not.
  const rememberedChatSearchRef = useRef('');
  const pendingRestoreRef = useRef(false);
  const explicitChatIntent = Boolean(location.state?.startNewChat || location.state?.openOnboarding);
  const restoringChatSearch = activeTab === 'chat'
    && pendingRestoreRef.current
    && !explicitChatIntent
    && location.search === ''
    && rememberedChatSearchRef.current !== '';
  // Bumped when Profile asks for the onboarding chat (redo).
  const [onboardingChatRequest, setOnboardingChatRequest] = useState(0);

  useEffect(() => {
    onboardingAPI.getState()
      .then((stateRes) => {
        setOnboardingComplete(Boolean(stateRes.onboarding_completed_at));
        setNeedsLeavingYear(Boolean(stateRes.needs_leaving_year));
        setOnboardingLoaded(true);
      })
      .catch(() => {
        setOnboardingLoaded(true);
      });
  }, []);

  const appsPaneRef = useRef(null);
  const casesPaneRef = useRef(null);

  useEffect(() => {
    const brand = isPathbuddyHost() ? 'Pathbuddy' : 'Ivori';
    const titles = {
      chat: `Chat — ${brand}`,
      apps: `Apps — ${brand}`,
      cases: `Cases — ${brand}`,
    };
    document.title = titles[activeTab] || titles.chat;

    // Apps/Cases own their own scrollbox (ChatShell.css), so router.js's
    // global blur+window.focus() on route change doesn't route keyboard
    // scroll to them. Deferred via setTimeout, not called synchronously:
    // ChatShell is a descendant of AppRouter, so React fires this effect
    // before AppRouter's — a synchronous focus() here would just get
    // undone when AppRouter's effect blurs it a moment later in the same
    // commit. A macrotask runs after that synchronous flush completes.
    // Chat is deliberately excluded — ChatAgentPanel already autoFocuses
    // its own input, which this would otherwise steal focus from.
    const paneRefs = { apps: appsPaneRef, cases: casesPaneRef };
    const timeoutId = setTimeout(() => {
      paneRefs[activeTab]?.current?.focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [activeTab]);

  useEffect(() => {
    if (!onboardingLoaded || onboardingComplete) return;
    if (activeTab === 'cases' && instructorCasesPermitted) return;
    if (activeTab === 'apps' || activeTab === 'cases') {
      navigate('/chat', { replace: true });
    }
  }, [onboardingLoaded, onboardingComplete, activeTab, navigate, instructorCasesPermitted]);

  // Remember /chat's query and put it back when a tab returns to Chat with
  // an empty search. A deep link that already carries a query is stored
  // instead, so it wins. startNewChat and openOnboarding win too: they
  // clear the memory so the replace below cannot be undone.
  useEffect(() => {
    if (activeTab !== 'chat') {
      pendingRestoreRef.current = true;
      return;
    }
    if (explicitChatIntent) {
      rememberedChatSearchRef.current = '';
      pendingRestoreRef.current = false;
      return;
    }
    const remembered = rememberedChatSearchRef.current;
    if (pendingRestoreRef.current && location.search === '' && remembered) {
      pendingRestoreRef.current = false;
      navigate({ pathname: '/chat', search: remembered }, { replace: true });
      return;
    }
    pendingRestoreRef.current = false;
    rememberedChatSearchRef.current = location.search || '';
  }, [activeTab, explicitChatIntent, location.search, navigate]);

  useEffect(() => {
    if (activeTab !== 'chat' || !location.state?.startNewChat) return;
    setNewChatRequest((n) => n + 1);
    navigate(location.pathname, { replace: true, state: {} });
  }, [activeTab, location.state, location.pathname, navigate]);

  // Profile's "Redo onboarding" / "Continue redo" ask for the onboarding chat
  // the same way; an open redo alone never changes which chat opens.
  useEffect(() => {
    if (activeTab !== 'chat' || !location.state?.openOnboarding) return;
    setOnboardingChatRequest((n) => n + 1);
    navigate(location.pathname, { replace: true, state: {} });
  }, [activeTab, location.state, location.pathname, navigate]);

  // Completing onboarding sets primary_subject on the server, but the auth
  // context still holds the user from sign-in, and ProtectedRoute reads its
  // primary_subject: without a refresh every protected page redirects back
  // to /chat until the browser reloads. GET /api/auth/me reads it fresh.
  // The signup flag ProtectedRoute pairs it with is cleared too: a set that
  // asks no subjects (school staff) leaves primary_subject 'undecided', which
  // would otherwise keep redirecting a finished user to /chat.
  const handleOnboardingComplete = async () => {
    setOnboardingComplete(true);
    try {
      sessionStorage.removeItem('needs_onboarding');
    } catch (e) {
      // Storage can be unavailable (private mode); ProtectedRoute then has no flag to read.
    }
    window.dispatchEvent(new CustomEvent(ONBOARDING_COMPLETE_EVENT));
    try {
      updateUser(await authAPI.getCurrentUser());
    } catch (e) {
      console.error('Failed to refresh user after onboarding completion:', e);
    }
  };

  return (
    <div className="chat-shell">
      <div className="chat-shell-panes">
        {isAuthenticated && onboardingComplete ? <ChooseExamDialog /> : null}
        {activeTab === 'chat' && onboardingComplete && needsLeavingYear ? <LeavingYearPrompt /> : null}
        <div className={`chat-shell-pane chat-shell-pane--chat${activeTab === 'chat' ? ' active' : ''}`}>
          <ChatAgentPanel
            fullPage
            onOnboardingComplete={handleOnboardingComplete}
            isChatTabActive={activeTab === 'chat'}
            newChatRequest={newChatRequest}
            onboardingChatRequest={onboardingChatRequest}
            pathsEnabled={activeTab === 'chat' && !restoringChatSearch}
          />
        </div>
        <div ref={appsPaneRef} tabIndex={-1} className={`chat-shell-pane chat-shell-pane--apps${activeTab === 'apps' ? ' active' : ''}`}>
          <UserPurchasedAppsPage />
        </div>
        <div ref={casesPaneRef} tabIndex={-1} className={`chat-shell-pane chat-shell-pane--cases${activeTab === 'cases' ? ' active' : ''}`}>
          <CaseStudiesPage />
        </div>
      </div>
    </div>
  );
}

export default ChatShell;
